// Helpers for sending the warehouse login screens to the single customer login page on the
// portal domain. Off unless the build sets VITE_UNIFIED_LOGIN=on and VITE_PORTAL_URL, so the
// switch evening flips it with a rebuild and a rollback flips it back the same way.
import { safeRelativePath } from './sso.helpers.js';

/** The portal origin from VITE_PORTAL_URL, or null when it is missing or not an http(s) URL. */
export const portalOrigin = value => {
  if (typeof value !== 'string' || !value.trim()) return null;
  try {
    const url = new URL(value.trim());
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
    return url.origin;
  } catch {
    return null;
  }
};

/** True when the build turned the redirect on and says where the portal is. */
export const unifiedLoginEnabled = env =>
  env?.VITE_UNIFIED_LOGIN === 'on' && !!portalOrigin(env?.VITE_PORTAL_URL);

/**
 * Where a warehouse login screen should send the browser, or null to render the screen as before.
 *
 * - `page`: 'login' or 'forgot-password'.
 * - `redirectUrl`: the warehouse path the user was on; passed as `next` when it is a safe
 *   same-origin path, so the portal can hand the user back to it after sign-in.
 * - `authenticated`: a warehouse session already exists; the login screen sends it into the app.
 * - `handoffFailed`: the /sso landing could not sign the user in. Bouncing back to the portal
 *   could hand off again and loop, so the warehouse page shows the reason instead.
 */
export const unifiedLoginTarget = ({
  env,
  page,
  redirectUrl,
  authenticated = false,
  handoffFailed = false,
}) => {
  if (!unifiedLoginEnabled(env) || authenticated || handoffFailed) return null;
  if (page !== 'login' && page !== 'forgot-password') return null;

  const url = new URL(`/${page}`, portalOrigin(env.VITE_PORTAL_URL));
  url.searchParams.set('app', 'wms');
  const next = page === 'login' ? safeRelativePath(redirectUrl) : null;
  if (next) url.searchParams.set('next', next);
  return url.toString();
};

/**
 * The "Back to portal" link of the user menu (EN-3138): the portal's single login page with
 * `app=portal`, so a user with both a portal and a WMS account lands in the portal instead of
 * being sent back to the app they used last. Null (hide the link) when unified login is off or
 * the portal URL is missing, so the menu is unchanged until the switch evening.
 */
export const portalLoginUrl = env => {
  if (!unifiedLoginEnabled(env)) return null;
  const url = new URL('/login', portalOrigin(env.VITE_PORTAL_URL));
  url.searchParams.set('app', 'portal');
  return url.toString();
};

/**
 * EN-4104: the portal's /handoff landing for a one-time code, in the URL fragment (never the query
 * string, as on this app's /sso landing). Null when unified login is off or there is no code.
 */
export const portalHandoffUrl = (env, code) => {
  if (!unifiedLoginEnabled(env) || typeof code !== 'string' || !code) return null;
  const url = new URL('/handoff', portalOrigin(env.VITE_PORTAL_URL));
  url.hash = new URLSearchParams({ code }).toString();
  return url.toString();
};

/**
 * EN-4104: "Back to portal" in one click. Asks wms-api for a one-time portal code and goes to the
 * portal's /handoff landing with it; on any failure (no portal account, 2FA needed on the portal,
 * an API without the route yet) goes to the plain portal login link instead, as before EN-4104.
 * Resolves to the URL it navigated to, or null when unified login is off (the item is hidden).
 */
export const goBackToPortal = async ({ env, requestCode, assign }) => {
  const fallback = portalLoginUrl(env);
  if (!fallback) return null;
  let target = fallback;
  try {
    const response = await requestCode();
    target = portalHandoffUrl(env, response?.code) || fallback;
  } catch {
    target = fallback;
  }
  assign(target);
  return target;
};
